import { motion } from "framer-motion";
import { Boxes } from "lucide-react";

/* SVG convergence: many fragmented nodes -> one central system. */
const SOURCES = [
  "Clienti",
  "Preventivi",
  "Ordini",
  "Documenti",
  "Comunicazioni",
  "Pagamenti",
  "Report",
];

export default function ConvergenceVisual() {
  const W = 460;
  const H = 380;
  const targetX = W - 70;
  const targetY = H / 2;
  const n = SOURCES.length;

  return (
    <div
      data-testid="convergence-visual"
      className="relative h-full overflow-hidden rounded-[18px] border border-black/10 bg-white p-5"
    >
      <span className="font-mono-tech text-[11px] uppercase tracking-[0.18em] text-emerald-700">
        La soluzione
      </span>
      <h3 className="mt-2 text-lg font-semibold tracking-[-0.02em] text-[color:var(--text-primary)]">
        Tutto converge in un unico sistema
      </h3>

      <div className="mt-4">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="h-auto w-full"
          role="img"
          aria-label="Strumenti frammentati che convergono in un unico sistema"
        >
          <defs>
            <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgba(2,12,8,0.14)" />
              <stop offset="100%" stopColor="rgba(5,150,105,0.75)" />
            </linearGradient>
          </defs>

          {SOURCES.map((label, i) => {
            const y = 40 + (i * (H - 80)) / (n - 1);
            const x = 20;
            const c1x = W / 2;
            const path = `M ${x + 120} ${y} C ${c1x} ${y}, ${c1x} ${targetY}, ${targetX - 34} ${targetY}`;
            return (
              <g key={label}>
                <motion.path
                  d={path}
                  fill="none"
                  stroke="url(#lineGrad)"
                  strokeWidth="1.5"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 1,
                    delay: 0.15 + i * 0.09,
                    ease: [0.2, 0.8, 0.2, 1],
                  }}
                />
                {/* source chip */}
                <foreignObject x={x} y={y - 13} width={120} height={26}>
                  <div className="flex h-[26px] items-center justify-center rounded-md border border-black/10 bg-[var(--ink-850)] px-2 text-[10px] text-secondary">
                    {label}
                  </div>
                </foreignObject>
                <motion.circle
                  cx={targetX - 34}
                  cy={targetY}
                  r="2.5"
                  fill="#34D399"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1 + i * 0.05 }}
                />
              </g>
            );
          })}

          {/* central node */}
          <motion.g
            initial={{ opacity: 0, scale: 0.85 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
            style={{ transformOrigin: `${targetX}px ${targetY}px` }}
          >
            <circle
              cx={targetX}
              cy={targetY}
              r="46"
              fill="rgba(16,185,129,0.10)"
              stroke="rgba(52,211,153,0.55)"
              strokeWidth="1.5"
            />
            <circle
              cx={targetX}
              cy={targetY}
              r="46"
              fill="none"
              stroke="rgba(52,211,153,0.25)"
              strokeWidth="10"
              className="animate-pulse"
            />
          </motion.g>
        </svg>
      </div>

      <div className="mt-2 flex items-center justify-end gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-emerald-600/15 text-emerald-700">
          <Boxes className="h-4 w-4" />
        </span>
        <span className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-[color:var(--text-primary)]">
          Un unico sistema
        </span>
      </div>
    </div>
  );
}
